import { useCallback } from "react"
import { BookmarkCheck, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { BankSuggestionMatch } from "@/stores/strategy-store"

interface BankSuggestionTierProps {
  matches: BankSuggestionMatch[]
  onAdd: (bankThemeId: string) => void
}

const CATEGORY_LABELS: Record<string, string> = {
  experience: 'Experience',
  personnel: 'Personnel',
  methodology: 'Methodology',
  innovation: 'Innovation',
  relationship: 'Relationship',
  value: 'Value',
  risk: 'Risk',
  local_presence: 'Local presence',
  other: 'Other',
}

function trackRecordLabel(timesUsed: number, timesWon: number): string {
  if (timesUsed === 0) return 'Not yet used'
  const winRate = timesWon / timesUsed
  if (winRate >= 0.5 && timesUsed >= 4) return `Proven · ${timesWon} of ${timesUsed} wins`
  return `Used in ${timesUsed} bid${timesUsed === 1 ? '' : 's'} · ${timesWon} win${timesWon === 1 ? '' : 's'}`
}

export function BankSuggestionTier({ matches, onAdd }: BankSuggestionTierProps) {
  const handleAdd = useCallback(
    (bankThemeId: string) => () => onAdd(bankThemeId),
    [onAdd]
  )

  if (matches.length === 0) return null

  return (
    <div className="rounded-xl bg-muted/60 border border-dashed border-border p-4">
      <div className="flex items-start gap-3 mb-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand))]">
          <BookmarkCheck className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Also consider from your theme bank</div>
          <div className="text-xs mt-0.5 text-muted-foreground">
            These themes match this tender and have a track record in past bids.
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {matches.map(match => (
          <div
            key={match.bankThemeId}
            className="rounded-xl border border-border bg-card shadow-sm p-3 flex items-start justify-between gap-3"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-sm font-medium">{match.title}</span>
                <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium leading-none bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand))]">
                  <BookmarkCheck className="h-2.5 w-2.5" />
                  {trackRecordLabel(match.timesUsed, match.timesWon)}
                </span>
              </div>
              {match.matchReason && (
                <p className="text-meta mb-1 leading-relaxed">{match.matchReason}</p>
              )}
              <div className="text-meta">
                {CATEGORY_LABELS[match.category] ?? match.category}
                {match.matchedCriteriaIds.length > 0 && (
                  <> · matches {match.matchedCriteriaIds.length} {match.matchedCriteriaIds.length === 1 ? 'criterion' : 'criteria'}</>
                )}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleAdd(match.bankThemeId)}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add to bid
            </Button>
          </div>
        ))}
      </div>
    </div>
  )
}
